// Permissions hook - combines user role and plan limits
import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import {
  hasPermission,
  hasPlanFeature,
  isWithinLimit,
  PLAN_LIMITS,
  type PlanLimits,
  type PricingPlan,
  type UserRole,
} from '@/types';

export interface PermissionCheck {
  // Role-based permissions
  canManageOrg: boolean;
  canManageBilling: boolean;
  canManageMembers: boolean;
  canManageSettings: boolean;

  // Project permissions
  canCreateProject: boolean;
  canDeleteProject: boolean;
  canManageProjectSettings: boolean;

  // Ticket permissions
  canViewTickets: boolean;
  canManageTickets: boolean;
  canAssignTickets: boolean;
  canDeleteTickets: boolean;

  // Article permissions
  canViewArticles: boolean;
  canCreateArticles: boolean;
  canEditArticles: boolean;
  canDeleteArticles: boolean;
  canPublishArticles: boolean;

  // Feature request permissions
  canViewFeatures: boolean;
  canManageFeatures: boolean;
  canDeleteFeatures: boolean;

  // Analytics permissions
  canViewAnalytics: boolean;
  canExportAnalytics: boolean;

  // Plan-based feature access
  hasCustomBranding: boolean;
  hasCustomDomain: boolean;
  hasScreenshotCapture: boolean;
  hasSessionRecording: boolean;
  hasKnowledgeBase: boolean;
  hasFeatureVoting: boolean;
  hasApiAccess: boolean;
  hasSlackIntegration: boolean;
  hasSso: boolean;
  hasAuditLogs: boolean;
  hasPrioritySupport: boolean;
  canRemoveWatermark: boolean;
  hasAdvancedAnalytics: boolean;

  // Plan limits
  limits: PlanLimits;
  plan: PricingPlan;
  role: UserRole;

  // Limit checkers
  canCreateMoreProjects: (currentCount: number) => boolean;
  canAddMoreSeats: (currentCount: number) => boolean;
  canCreateMoreArticles: (currentCount: number) => boolean;
  canCreateMoreTickets: (currentCount: number) => boolean;

  // Generic permission checker
  can: (permission: string) => boolean;
  hasFeature: (feature: string) => boolean;
}

export const usePermissions = (): PermissionCheck => {
  const { profile, isSuperAdmin } = useAuth();
  const { organization, member } = useOrganization();

  return useMemo(() => {
    // Default role and plan for when not loaded
    const role: UserRole = isSuperAdmin ? 'super_admin' : (member?.role ?? profile?.role ?? 'viewer');
    const plan: PricingPlan = organization?.plan ?? 'free';
    const limits = organization?.limits ?? PLAN_LIMITS.free;

    // Helper for role-based permission check
    const can = (permission: string): boolean => {
      if (isSuperAdmin) return true;
      return hasPermission(role, permission);
    };

    // Helper for plan-based feature check
    const hasFeature = (feature: string): boolean => {
      return hasPlanFeature(plan, feature);
    };

    // Helper for limit check
    const checkLimit = (limitType: keyof PlanLimits, currentCount: number): boolean => {
      return isWithinLimit(plan, limitType, currentCount);
    };

    return {
      // Role-based permissions
      canManageOrg: can('org:manage'),
      canManageBilling: can('org:billing'),
      canManageMembers: can('org:members'),
      canManageSettings: can('org:settings'),

      // Project permissions
      canCreateProject: can('project:create'),
      canDeleteProject: can('project:delete'),
      canManageProjectSettings: can('project:settings'),

      // Ticket permissions
      canViewTickets: can('ticket:view'),
      canManageTickets: can('ticket:manage'),
      canAssignTickets: can('ticket:assign'),
      canDeleteTickets: can('ticket:delete'),

      // Article permissions
      canViewArticles: can('article:view'),
      canCreateArticles: can('article:create'),
      canEditArticles: can('article:edit'),
      canDeleteArticles: can('article:delete'),
      canPublishArticles: can('article:publish'),

      // Feature request permissions
      canViewFeatures: can('feature:view'),
      canManageFeatures: can('feature:manage'),
      canDeleteFeatures: can('feature:delete'),

      // Analytics permissions
      canViewAnalytics: can('analytics:view'),
      canExportAnalytics: can('analytics:export'),

      // Plan-based feature access
      hasCustomBranding: hasFeature('custom_branding'),
      hasCustomDomain: hasFeature('custom_domain'),
      hasScreenshotCapture: hasFeature('screenshot_capture'),
      hasSessionRecording: hasFeature('session_recording'),
      hasKnowledgeBase: hasFeature('knowledge_base'),
      hasFeatureVoting: hasFeature('feature_voting'),
      hasApiAccess: hasFeature('api_access'),
      hasSlackIntegration: hasFeature('slack_integration'),
      hasSso: hasFeature('sso'),
      hasAuditLogs: hasFeature('audit_logs'),
      hasPrioritySupport: hasFeature('priority_support'),
      canRemoveWatermark: hasFeature('remove_watermark'),
      hasAdvancedAnalytics: hasFeature('advanced_analytics'),

      // Plan limits
      limits,
      plan,
      role,

      // Limit checkers
      canCreateMoreProjects: (currentCount: number) => checkLimit('projects', currentCount),
      canAddMoreSeats: (currentCount: number) => checkLimit('seats', currentCount),
      canCreateMoreArticles: (currentCount: number) => checkLimit('knowledgeBaseArticles', currentCount),
      canCreateMoreTickets: (currentCount: number) => checkLimit('monthlyTickets', currentCount),

      // Generic checkers
      can,
      hasFeature,
    };
  }, [profile, isSuperAdmin, organization, member]);
};

// Hook for checking a single permission
export const useCanDo = (permission: string): boolean => {
  const { can } = usePermissions();
  return can(permission);
};

// Hook for checking a single feature
export const useHasFeature = (feature: string): boolean => {
  const { hasFeature } = usePermissions();
  return hasFeature(feature);
};
